import { CameraOutlined } from '@ant-design/icons';
import { Col, Flex, Form, Input, Modal, Row, Select, Switch, Typography, Upload } from 'antd';
import { useEffect, useState } from 'react';
import { HiOutlineTrash } from 'react-icons/hi';
import { rolesOption } from '../../constants';
import { generateUniqueId, isAdmin, isManager, isSuperAdmin } from '../../helper';
import { addRestaurantUser, getUsers, loggedInUser, registerUser, updateUser } from '../../redux/actions/auth';
import { booleanToInteger, getFileUrl, integerToBoolean } from '../../utils';
import { generatePassword } from '../../utils/generatePassword';
import { getUploadUrl, uploadFileToS3 } from '../../redux/actions/upload';
import { sendAccountApprovedEmail, sendPasswordChangedEmail, sendStoreAssignedEmail, sendUserCreds } from '../../redux/actions/email';
import useRestaurants from '../../hooks/useRestaurants';
import _ from 'lodash';

const ManageUserModal = ({ open, onCancel, userData, isProfile }) => {
  const { restaurants } = useRestaurants();
  const [form] = Form.useForm();
  const [uploadedFile, setUploadedFile] = useState([]);
  const [formattedFile, setFormattedFile] = useState([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isResetPassword, setIsResetPassword] = useState(false);

  const handleUpload = ({ fileList }) => {
    const file = fileList?.at(0);
    if (file) {
      const uniqueName = `${file?.name?.split('.')?.at(0)}-${generateUniqueId()}.${file?.name?.split('.').at(1)}`;
      const formatFile = {
        ContentType: file?.type,
        display_name: uniqueName,
        file: file?.originFileObj,
      };
      setFormattedFile(formatFile);
    } else {
      setFormattedFile([]);
    }
    setUploadedFile(fileList);
  };

  const handleSwitchChange = (checked) => {
    setIsEnabled(checked);
  };

  const handleResetPasswordSwitchChange = (checked) => {
    setIsResetPassword(checked);
  };

  const shopOptions = restaurants?.map((shop) => {
    return {
      label: shop?.title,
      value: shop?.id,
    };
  });

  const getOptions = () => {
    if (isProfile) return rolesOption;
    return rolesOption.filter((option) => {
      if (option.value === 0) return false;
      if (isAdmin()) {
        return option.value !== 1;
      } else if (isManager()) {
        return option.value !== 1 && option.value !== 2;
      }
      return true;
    });
  };

  const handleFinish = async (values) => {
    const { confirmPassword, profilePhoto: _unused, ...body } = values;

    const profilePhoto = formattedFile?.display_name
      ? JSON.stringify([formattedFile?.display_name])
      : userData && uploadedFile.length
        ? userData.profile_photo
        : null;
    const updatedBody = {
      ...body,
      profile_photo: profilePhoto,
      status: booleanToInteger(isEnabled),
      ...(userData ? { id: userData.id } : { password: generatePassword(16) }),
    };

    const res = userData
      ? await updateUser(updatedBody, false, { isFetch: false })
      : await registerUser(updatedBody, { isFetch: false });

    const userId = userData?.id ?? res?.record_id

    if (res?.ack) {
      if (isEnabled && userData && userData?.status !== 1 && !isProfile) {
        sendAccountApprovedEmail({
          user: {
            first_name: values?.first_name,
            last_name: values?.last_name ?? '',
          },
          userEmail: values?.email
        })
      } else if (isResetPassword && isProfile) {
        sendPasswordChangedEmail({
          user: {
            first_name: values?.first_name,
            last_name: values?.last_name ?? '',
          },
          userEmail: values?.email
        })
      }
      await addRestaurantUser({
        userId: userId,
        restaurant_ids: values.shop,
      });

      const restaurantIds = _.difference(values?.shop, userData?.restaurants);
      if (restaurantIds?.length) {
        sendStoreAssignedEmail({
          userIds: [userId],
          restaurantIds,
          type: 'USERS'
        });
      }
      getUsers();
    }

    if (res?.ack && !userData && !isProfile)
      sendUserCreds({
        userName: `${updatedBody?.first_name} ${updatedBody?.last_name ?? ''}`,
        email: updatedBody?.email,
        password: updatedBody?.password,
      });

    if (res && formattedFile?.file) {
      const uploadPromises = [];
      uploadPromises.push(
        getUploadUrl(formattedFile, 'users', res?.record_id ?? userData.id)
          .then((uploadUrl) =>
            uploadFileToS3(uploadUrl, formattedFile.file, formattedFile.file.type)
          )
          .catch((error) => console.error('Error uploading file:', error))
      );
      await Promise.all(uploadPromises);
    }
    if (isProfile && res?.ack) await loggedInUser();
    if (res?.ack) onCancel();
  };

  useEffect(() => {
    if (userData && open) {
      const { first_name, last_name, email, phone, restaurants, profile_photo, role, status } =
        userData;
      setIsEnabled(integerToBoolean(status));
      form.setFieldsValue({
        first_name,
        last_name,
        email,
        phone,
        shop: Array.isArray(restaurants) ? restaurants : restaurants ? [restaurants] : [],
        role,
      });
      if (profile_photo) {
        setUploadedFile([
          {
            uid: '-1',
            name: 'Profile Photo',
            status: 'done',
            url: getFileUrl('users', userData?.id, JSON.parse(profile_photo)),
          },
        ]);
      }
    }
  }, [userData, open, form]);

  return (
    <Modal
      title={isProfile ? 'Update Profile' : userData ? 'Edit User' : 'Add User'}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      okText={isProfile ? 'Update Profile' : userData ? 'Save Changes' : 'Add User'}
      centered
      width={isProfile ? 600 : 700}
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: '#00000015',
        },
        content: {
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <div style={{
          background: '#f8fafc',
          padding: '20px 24px',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0'
        }}>
          <Flex align="center" gap={32}>
            <Form.Item
              name="profilePhoto"
              getValueFromEvent={({ fileList }) => fileList}
              style={{ marginBottom: 0 }}
            >
              <Upload
                name="file"
                listType="picture-circle"
                fileList={uploadedFile}
                beforeUpload={() => false}
                onChange={handleUpload}
                maxCount={1}
                showUploadList={{
                  showRemoveIcon: true,
                  showPreviewIcon: false,
                  removeIcon: <HiOutlineTrash style={{ color: '#fff', fontSize: 20 }} />,
                }}
                accept=".png,.jpg,.avif,.webp,.jpeg"
              >
                {uploadedFile.length === 0 && <CameraOutlined style={{ fontSize: 34 }} />}
              </Upload>
            </Form.Item>

            <Flex gap={16} justify="end" style={{ flex: 1 }}>
              {(isProfile || (userData && (isSuperAdmin() || isAdmin()))) && (
                <div
                  onClick={() => handleResetPasswordSwitchChange(!isResetPassword)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: isResetPassword ? '#1677ff' : '#e2e8f0',
                    background: isResetPassword ? '#e6f4ff' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    flex: 1,
                    maxWidth: '240px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Flex justify="space-between" align="center" gap={4}>
                    <Flex vertical>
                      <Typography.Text strong style={{ fontSize: '13px', color: isResetPassword ? '#003a8c' : '#1f2937' }}>
                        Reset Password
                      </Typography.Text>
                      <Typography.Text style={{ fontSize: '11px', color: isResetPassword ? '#0958d9' : '#6b7280' }}>
                        Update credentials
                      </Typography.Text>
                    </Flex>
                    <Switch checked={isResetPassword} size="small" />
                  </Flex>
                </div>
              )}
              {!isProfile && (
                <div
                  onClick={() => handleSwitchChange(!isEnabled)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: isEnabled ? '#059669' : '#dc2626',
                    background: isEnabled ? '#ecfdf5' : '#fef2f2',
                    cursor: 'pointer',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    flex: 1,
                    maxWidth: '240px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Flex justify="space-between" align="center" gap={4}>
                    <Flex vertical>
                      <Typography.Text strong style={{ fontSize: '13px', color: isEnabled ? '#064e3b' : '#7f1d1d' }}>
                        Account Status
                      </Typography.Text>
                      <Typography.Text style={{ fontSize: '11px', color: isEnabled ? '#059669' : '#dc2626' }}>
                        {isEnabled ? 'User is Active' : 'User is Inactive'}
                      </Typography.Text>
                    </Flex>
                    <Switch checked={isEnabled} size="small" />
                  </Flex>
                </div>
              )}
            </Flex>
          </Flex>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: "Please enter the user's first name!" }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Last Name" name="last_name">
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: "Please enter the user's email!" },
            { type: 'email', message: 'Please enter a valid email!' },
          ]}
        >
          <Input disabled={isProfile} placeholder="Enter user email" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Phone No." name="phone">
              <Input placeholder="Enter phone number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: "Please select the user's role!" }]}
            >
              <Select disabled={isProfile} placeholder="Select role" options={getOptions()} />
            </Form.Item>
          </Col>
        </Row>

        {!isProfile && (
          <Form.Item label="Site" name="shop">
            <Select
              placeholder="Site"
              mode="multiple"
              options={shopOptions}
              allowClear
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}

        {isResetPassword && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: 'Please create a password!' },
                  { min: 6, message: 'Password must be at least 6 characters!' },
                ]}
              >
                <Input.Password placeholder="Create a password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm your password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter your password" />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

export default ManageUserModal;
